import React, { useState } from "react";
import { Link } from "react-router-dom";
import { siteConfig } from "../Datas/productData";
import "../css/contactus.scss";

const initialFormData = {
  firstName: "",
  lastName: "",
  email: "",
  phone: "",
  subject: "",
  message: "",
};

function Contact() {
  const [formData, setFormData] = useState(initialFormData);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [errors, setErrors] = useState({});
  const { contact, brandName } = siteConfig;

  const handleInputChange = (event) => {
    const { name, value } = event.target;
    setFormData((current) => ({ ...current, [name]: value }));
    setShowSuccess(false);

    if (errors[name]) {
      setErrors((current) => ({ ...current, [name]: "" }));
    }
  };

  const validateForm = () => {
    const nextErrors = {};
    const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

    if (!formData.firstName.trim()) {
      nextErrors.firstName = "Enter your first name.";
    }
    if (!formData.lastName.trim()) {
      nextErrors.lastName = "Enter your last name.";
    }
    if (!formData.email.trim()) {
      nextErrors.email = "Enter your email address.";
    } else if (!emailPattern.test(formData.email)) {
      nextErrors.email = "Enter a valid email address.";
    }
    if (!formData.subject) {
      nextErrors.subject = "Choose a subject.";
    }
    if (!formData.message.trim()) {
      nextErrors.message = "Tell us how we can help.";
    } else if (formData.message.trim().length < 10) {
      nextErrors.message = "Please add a little more detail.";
    }

    return nextErrors;
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    const nextErrors = validateForm();

    if (Object.keys(nextErrors).length > 0) {
      setErrors(nextErrors);
      const firstInvalidField = document.getElementById(
        Object.keys(nextErrors)[0]
      );
      firstInvalidField?.focus();
      return;
    }

    setErrors({});
    setIsSubmitting(true);

    window.setTimeout(() => {
      setFormData(initialFormData);
      setIsSubmitting(false);
      setShowSuccess(true);
    }, 650);
  };

  return (
    <main className="contact" id="main-content" tabIndex="-1">
      <header className="contact__header">
        <p>Client services</p>
        <h1>Contact</h1>
        <span>
          Questions about a product or an order? Our team is ready to help.
        </span>
      </header>

      <div className="contact__layout">
        <aside className="contact__details" aria-label="Contact information">
          <div className="contact__detail-block">
            <p>Email</p>
            <a href={`mailto:${contact.email}`}>{contact.email}</a>
          </div>
          {contact.phone && (
            <div className="contact__detail-block">
              <p>Telephone</p>
              <a href={`tel:${contact.phone.replace(/\s/g, "")}`}>
                {contact.phone}
              </a>
            </div>
          )}
          <div className="contact__detail-block">
            <p>Opening hours</p>
            <span>{contact.hours}</span>
          </div>
          <div className="contact__detail-block">
            <p>Studio</p>
            <address>{contact.address}</address>
          </div>
          <nav className="contact__quick-links" aria-label="Help topics">
            <Link to="/shipping">Shipping information</Link>
            <Link to="/returns">Returns &amp; exchanges</Link>
          </nav>
        </aside>

        <section className="contact__form-section" aria-labelledby="form-title">
          <div className="contact__form-heading">
            <p>Send a message</p>
            <h2 id="form-title">How can we help?</h2>
          </div>

          {showSuccess && (
            <div className="contact__success" role="status" aria-live="polite">
              <span aria-hidden="true">✓</span>
              <div>
                <strong>Thank you for getting in touch.</strong>
                <p>
                  Your message has been saved in this demo. The {brandName}
                  client service team would normally reply by email.
                </p>
              </div>
            </div>
          )}

          <form className="contact__form" onSubmit={handleSubmit} noValidate>
            <div className="contact__field-row">
              <div className="contact__field">
                <label htmlFor="firstName">First name *</label>
                <input
                  id="firstName"
                  name="firstName"
                  value={formData.firstName}
                  onChange={handleInputChange}
                  autoComplete="given-name"
                  aria-invalid={Boolean(errors.firstName)}
                  aria-describedby={errors.firstName ? "firstName-error" : undefined}
                />
                {errors.firstName && (
                  <small id="firstName-error">{errors.firstName}</small>
                )}
              </div>
              <div className="contact__field">
                <label htmlFor="lastName">Last name *</label>
                <input
                  id="lastName"
                  name="lastName"
                  value={formData.lastName}
                  onChange={handleInputChange}
                  autoComplete="family-name"
                  aria-invalid={Boolean(errors.lastName)}
                  aria-describedby={errors.lastName ? "lastName-error" : undefined}
                />
                {errors.lastName && (
                  <small id="lastName-error">{errors.lastName}</small>
                )}
              </div>
            </div>

            <div className="contact__field-row">
              <div className="contact__field">
                <label htmlFor="email">Email *</label>
                <input
                  id="email"
                  name="email"
                  type="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  autoComplete="email"
                  aria-invalid={Boolean(errors.email)}
                  aria-describedby={errors.email ? "email-error" : undefined}
                />
                {errors.email && <small id="email-error">{errors.email}</small>}
              </div>
              <div className="contact__field">
                <label htmlFor="phone">Telephone (optional)</label>
                <input
                  id="phone"
                  name="phone"
                  type="tel"
                  value={formData.phone}
                  onChange={handleInputChange}
                  autoComplete="tel"
                />
              </div>
            </div>

            <div className="contact__field">
              <label htmlFor="subject">Subject *</label>
              <select
                id="subject"
                name="subject"
                value={formData.subject}
                onChange={handleInputChange}
                aria-invalid={Boolean(errors.subject)}
                aria-describedby={errors.subject ? "subject-error" : undefined}
              >
                <option value="" disabled>
                  Select a topic
                </option>
                <option value="order">Order status</option>
                <option value="product">Product &amp; sizing</option>
                <option value="delivery">Delivery</option>
                <option value="return">Return or exchange</option>
                <option value="other">Something else</option>
              </select>
              {errors.subject && (
                <small id="subject-error">{errors.subject}</small>
              )}
            </div>

            <div className="contact__field">
              <label htmlFor="message">Message *</label>
              <textarea
                id="message"
                name="message"
                rows="6"
                maxLength="800"
                value={formData.message}
                onChange={handleInputChange}
                aria-invalid={Boolean(errors.message)}
                aria-describedby={errors.message ? "message-error" : undefined}
              />
              <div className="contact__field-meta">
                {errors.message ? (
                  <small id="message-error">{errors.message}</small>
                ) : (
                  <span />
                )}
                <span>{formData.message.length}/800</span>
              </div>
            </div>

            <button type="submit" disabled={isSubmitting}>
              {isSubmitting ? "Sending…" : "Send message"}
            </button>
            <p className="contact__privacy-note">
              By sending this form, you agree that we may use your details to
              answer your request. See our <Link to="/privacy">privacy policy</Link>.
            </p>
          </form>
        </section>
      </div>
    </main>
  );
}

export default Contact;
